import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Snackbar,
  Checkbox,
  Dialog,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { supabase } from '../../services/supabase';
import type { League, Player } from '../../types/models';

interface PlayerConstraint {
  playerId: string;
  excludedGames: number[];
}

interface SeatAssignment {
  gameNumber: number;
  seats: {
    playerName: string;
    playerId: string;
    seatNumber: number;
    gamesPlayed?: number;
  }[];
}

// This represents the data structure to save to the database
interface SeatingPlan {
  id?: string;
  league_id: string;
  session_date: string;
  created_at?: string;
  games: {
    game_number: number;
    seats: {
      player_id: string;
      seat_number: number;
    }[];
  }[];
}

interface SeatGenerationToolProps {
  onPlanSaved?: () => void;
}

const SeatGenerationTool: React.FC<SeatGenerationToolProps> = ({ onPlanSaved }) => {
  const navigate = useNavigate();
  
  // State for form inputs
  const [leagues, setLeagues] = useState<League[]>([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');
  const [gameDate, setGameDate] = useState<Date | null>(new Date());
  const [availablePlayers, setAvailablePlayers] = useState<Player[]>([]);
  const [selectedPlayers, setSelectedPlayers] = useState<Player[]>([]);
  const [numGames, setNumGames] = useState<number>(5);
  const [playerConstraints, setPlayerConstraints] = useState<PlayerConstraint[]>([]);
  
  // State for generated data
  const [seatAssignments, setSeatAssignments] = useState<SeatAssignment[]>([]);


  // UI state
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  const [constraintsDialogOpen, setConstraintsDialogOpen] = useState(false);
  const [currentPlayerForConstraints, setCurrentPlayerForConstraints] = useState<Player | null>(null);

  // Fetch leagues on component mount
  useEffect(() => {
    fetchLeagues();
  }, []);

  // Fetch players when a league is selected
  useEffect(() => {
    if (selectedLeagueId) {
      fetchPlayersForLeague(selectedLeagueId);
    }
  }, [selectedLeagueId]);
  
  // Automatically select top 10 players by game count when players are loaded
  useEffect(() => {
    if (availablePlayers.length > 0 && selectedLeagueId) {
      // Sort players by games played descending
      const sortedPlayers = [...availablePlayers].sort(
        (a, b) => ((b as any).gamesPlayed || 0) - ((a as any).gamesPlayed || 0)
      );
      
      // Select the top 10 (or all if less than 10)
      const topPlayers = sortedPlayers.slice(0, Math.min(10, sortedPlayers.length));
      setSelectedPlayers(topPlayers);
    }
  }, [availablePlayers, selectedLeagueId]);

  const fetchLeagues = async () => {
    try {
      // Import the helpers we need
      const { fixTypes, getCurrentUserRole, isSuperAdmin } = await import('../../services/adminPermissions');
      
      // Start with base query
      let baseQuery = supabase
        .from('leagues')
        .select(`
          *,
          clubs (
            id,
            name
          ),
          judge:judge_id (
            id,
            nickname,
            full_name
          )
        `);
      
      // Check if user is a superadmin
      const superAdmin = await isSuperAdmin();
      let queryResult;
      
      if (superAdmin) {
        // Superadmins see all leagues
        queryResult = await baseQuery.order('name', { ascending: true });
      } else {
        // Regular admins only see leagues from their club
        const userRole = await getCurrentUserRole();
        
        if (!userRole?.club_id) {
          // No club assigned, return empty result
          queryResult = { data: [], error: null };
        } else {
          // Filter leagues by the admin's club
          queryResult = await baseQuery
            .eq('club_id', userRole.club_id)
            .order('name', { ascending: true });
        }
      }
      
      const { data, error } = queryResult;

      if (error) {
        throw error;
      }

      // Use the fixTypes helper to ensure correct typing
      const typedData = fixTypes<League>(data || []);
      
      setLeagues(typedData);
    } catch (error: any) {
      console.error('Error fetching leagues:', error.message);
      showSnackbar('Failed to load leagues', 'error');
    }
  };

  const fetchPlayersForLeague = async (leagueId: string) => {
    setLoading(true);
    try {
      // Get all players
      const { data: playersData, error: playersError } = await supabase
        .from('players')
        .select('*')
        .order('nickname', { ascending: true });

      if (playersError) {
        throw playersError;
      }

      // Get games played counts for this league
      const { data: gameCountsData, error: gameCountsError } = await supabase
        .from('games')
        .select(`
          id,
          league_id,
          game_results (
            player_id,
            player:player_id (
              id,
              nickname
            )
          )
        `)
        .eq('league_id', leagueId);

      if (gameCountsError) {
        throw gameCountsError;
      }

      // Calculate games played per player in this league
      const playerGamesCount: Record<string, number> = {};
      
      (gameCountsData || []).forEach(game => {
        (game.game_results || []).forEach((result: any) => {
          const playerId = result.player_id;
          playerGamesCount[playerId] = (playerGamesCount[playerId] || 0) + 1;
        });
      });

      // Attach games played count to player objects
      const playersWithStats = (playersData || []).map((player: Player) => ({
        ...player,
        gamesPlayed: playerGamesCount[player.id] || 0
      }));

      setAvailablePlayers(playersWithStats);
    } catch (error: any) {
      console.error('Error fetching players for league:', error.message);
      showSnackbar('Failed to load players', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLeagueChange = (event: SelectChangeEvent) => {
    setSelectedLeagueId(event.target.value);
    // Reset players and selections when league changes
    setAvailablePlayers([]);
    setSelectedPlayers([]);
    setSeatAssignments([]);
  };

  const handlePlayersChange = (_event: React.SyntheticEvent | null, newValue: Player[]) => {
    setSelectedPlayers(newValue);
    setSeatAssignments([]);
  };

  const handleNumGamesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.max(1, Math.min(20, parseInt(event.target.value) || 1));
    setNumGames(value);
    
    // Update constraints to match the new number of games
    // Remove constraints for games that no longer exist
    setPlayerConstraints(prevConstraints => 
      prevConstraints.map(constraint => ({
        ...constraint,
        excludedGames: constraint.excludedGames.filter(game => game <= value)
      }))
    );
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  const openConstraintsDialog = (player: Player) => {
    setCurrentPlayerForConstraints(player);
    setConstraintsDialogOpen(true);
  };
  
  const closeConstraintsDialog = () => {
    setCurrentPlayerForConstraints(null);
    setConstraintsDialogOpen(false);
  };
  
  const handleApproveSeating = async () => {
    if (!selectedLeagueId || !seatAssignments.length || !gameDate) {
      showSnackbar('Missing required information for saving seating plan', 'error');
      return;
    }
    
    setLoading(true);
    
    try {
      // Create a properly formatted SeatingPlan object from the current assignments
      const seatingPlan: SeatingPlan = {
        league_id: selectedLeagueId,
        session_date: gameDate.toISOString().split('T')[0], // Format as YYYY-MM-DD
        games: seatAssignments.map(game => ({
          game_number: game.gameNumber,
          seats: game.seats.map(seat => ({
            player_id: seat.playerId,
            seat_number: seat.seatNumber
          }))
        }))
      };
      
      // Check if the seating_plans table exists
      const { error: tableCheckError } = await supabase
        .from('seating_plans')
        .select('id')
        .limit(1);
      
      // If the table doesn't exist, we need to create it
      if (tableCheckError && tableCheckError.message.includes('relation "seating_plans" does not exist')) {
        showSnackbar('The seating_plans table needs to be created. Please contact an administrator.', 'error');
        console.error('seating_plans table does not exist. Run appropriate migrations first.');
        setLoading(false);
        return;
      }
      
      // Save the seating plan
      const { data, error } = await supabase
        .from('seating_plans')
        .insert([seatingPlan])
        .select();
      
      if (error) throw error;
      
      // Get the ID of the newly created plan
      const planId = data?.[0]?.id;
      
      if (!planId) {
        throw new Error('Failed to get ID of newly created seating plan');
      }
      
      showSnackbar('Seating plan saved successfully!', 'success');
      
      // Call the callback if provided
      if (onPlanSaved) {
        onPlanSaved();
      }
      
      // Navigate to the seating plan view page
      navigate(`/seating-plan/${planId}`);
      
    } catch (error: any) {
      console.error('Error saving seating plan:', error);
      showSnackbar(`Failed to save seating plan: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const handleToggleGameConstraint = (gameNumber: number) => {
    if (!currentPlayerForConstraints) return;
    
    const playerId = currentPlayerForConstraints.id;
    const existingConstraint = playerConstraints.find(c => c.playerId === playerId);
    
    if (existingConstraint) {
      // Update existing constraint
      setPlayerConstraints(prevConstraints => 
        prevConstraints.map(constraint => {
          if (constraint.playerId === playerId) {
            // Toggle the game in/out of excludedGames
            const isExcluded = constraint.excludedGames.includes(gameNumber);
            return {
              ...constraint,
              excludedGames: isExcluded
                ? constraint.excludedGames.filter(g => g !== gameNumber)
                : [...constraint.excludedGames, gameNumber]
            };
          }
          return constraint;
        })
      );
    } else {
      // Create new constraint
      setPlayerConstraints(prevConstraints => [
        ...prevConstraints,
        { playerId, excludedGames: [gameNumber] }
      ]);
    }
  };
  
  const getPlayerConstraints = (playerId: string): number[] => {
    const constraint = playerConstraints.find(c => c.playerId === playerId);
    return constraint ? constraint.excludedGames : [];
  };

  // Function to generate the seat assignments
  const generateSeats = () => {
    if (selectedPlayers.length < 10) {
      showSnackbar('Please select at least 10 players', 'error');
      return;
    }

    setLoading(true);

    try {
      // 1. Use the new miss distribution algorithm to determine which players miss which games
      // Calculate total amount of misses to be assigned
      const totalPlayers = selectedPlayers.length;
      const playersPerGame = 10;
      const totalMisses = (totalPlayers - playersPerGame) * numGames;
      
      // Create a list of players with their game counts and constraints
      const players = selectedPlayers.map(player => ({
        id: player.id,
        name: player.nickname,
        gamesPlayed: (player as any).gamesPlayed || 0,
        constraints: getPlayerConstraints(player.id),
        misses: 0,
        missedGames: [] as number[]
      }));
      
      // Assign misses for constraints first
      players.forEach(player => {
        const constraintMisses = player.constraints.length;
        player.misses = constraintMisses;
        // Initialize missedGames with constraints
        player.missedGames = [...player.constraints];
      });
      
      // Check if constraints exceed total misses
      const constraintMisses = players.reduce((sum, p) => sum + p.constraints.length, 0);
      if (constraintMisses > totalMisses) {
        throw new Error(`Too many constraints (${constraintMisses}) for the total misses (${totalMisses})`);
      }
      
      // Sort players by games played (most to least)
      players.sort((a, b) => b.gamesPlayed - a.gamesPlayed);
      
      // Calculate remaining misses to distribute
      let remainingMisses = totalMisses - constraintMisses;
      
      // Distribute remaining misses to players round by round
      while (remainingMisses > 0) {
        // One round of distributing misses
        for (let i = 0; i < players.length && remainingMisses > 0; i++) {
          // Skip players with constraints for this round
          if (players[i].constraints.length === 0) {
            players[i].misses += 1;
            remainingMisses -= 1;
          }
          
          if (remainingMisses === 0) break;
        }
      }
      
      // 2. Validate that each game has at least 10 players available
      for (let gameNum = 1; gameNum <= numGames; gameNum++) {
        const playersWithConstraint = players.filter(p => p.constraints.includes(gameNum));
        if (playersWithConstraint.length > totalPlayers - playersPerGame) {
          throw new Error(`Game ${gameNum} has too many player constraints (${playersWithConstraint.length}). Max allowed: ${totalPlayers - playersPerGame}`);
        }
      }
      
      // 3. Now determine which players miss which games, starting from the last game
      const remainingMissesPerPlayer = players.map(p => ({
        id: p.id,
        name: p.name,
        missedGames: [...p.missedGames], // Already includes constraints
        missesRemaining: p.misses - p.constraints.length, // Still need to assign this many misses
      }));
      
      // For each game, determine who will miss it
      for (let gameNum = numGames; gameNum >= 1; gameNum--) {
        // Calculate how many players need to miss this game
        const playersAlreadyMissing = remainingMissesPerPlayer.filter(p => 
          p.missedGames.includes(gameNum)
        ).length;
        
        const playersToAssign = totalPlayers - playersPerGame - playersAlreadyMissing;
        
        if (playersToAssign > 0) {
          // Create a list of eligible players (those who don't already have this game as a constraint)
          const eligiblePlayers = remainingMissesPerPlayer.filter(p => 
            !p.missedGames.includes(gameNum) && p.missesRemaining > 0
          );
          
          if (eligiblePlayers.length < playersToAssign) {
            throw new Error(`Not enough eligible players for game ${gameNum}. Need ${playersToAssign} more players to miss but only ${eligiblePlayers.length} are eligible.`);
          }
          
          // Sort by most misses remaining
          eligiblePlayers.sort((a, b) => b.missesRemaining - a.missesRemaining);
          
          // Assign misses in groups based on same miss count
          let stillNeeded = playersToAssign;
          let currentMissesRemaining = eligiblePlayers[0].missesRemaining;
          let currentGroup = eligiblePlayers.filter(p => p.missesRemaining === currentMissesRemaining);
          
          while (stillNeeded > 0) {
            // Shuffle current group
            for (let i = currentGroup.length - 1; i > 0; i--) {
              const j = Math.floor(Math.random() * (i + 1));
              [currentGroup[i], currentGroup[j]] = [currentGroup[j], currentGroup[i]];
            }
            
            // Take the required number of players from this group
            const playersToTake = Math.min(stillNeeded, currentGroup.length);
            const selectedPlayers = currentGroup.slice(0, playersToTake);
            
            // Assign the game as missed for these players
            selectedPlayers.forEach(p => {
              const playerIdx = remainingMissesPerPlayer.findIndex(rp => rp.id === p.id);
              if (playerIdx !== -1) {
                remainingMissesPerPlayer[playerIdx].missedGames.push(gameNum);
                remainingMissesPerPlayer[playerIdx].missesRemaining--;
              }
            });
            
            // Reduce the number still needed
            stillNeeded -= playersToTake;
            
            // If we still need more, move to the next group
            if (stillNeeded > 0) {
              currentMissesRemaining--;
              currentGroup = eligiblePlayers.filter(p => 
                p.missesRemaining === currentMissesRemaining && 
                !p.missedGames.includes(gameNum)
              );
              
              // If there's no more players at this level, we have a problem
              if (currentGroup.length === 0 && stillNeeded > 0) {
                throw new Error(`Cannot assign enough players to miss game ${gameNum}.`);
              }
            }
          }
        }
      }
      
      // 4. Update the original players array with the assigned missed games
      players.forEach(player => {
        const assignedPlayer = remainingMissesPerPlayer.find(p => p.id === player.id);
        if (assignedPlayer) {
          player.missedGames = assignedPlayer.missedGames;
        }
      });
      
      // 5. Now create the seat assignments based on who plays in which game
      const assignments: SeatAssignment[] = [];
      
      // Generate seating for each game
      for (let gameNum = 1; gameNum <= numGames; gameNum++) {
        // Get the players who are playing in this game (those who don't miss it)
        const playersInGame = players.filter(player => !player.missedGames.includes(gameNum));
        
        // Sanity check
        if (playersInGame.length !== playersPerGame) {
          throw new Error(`Game ${gameNum} has ${playersInGame.length} players assigned, but expected ${playersPerGame}.`);
        }
        
        // Assign random seat numbers (1-10)
        const seatNumbers = Array.from({length: playersPerGame}, (_, i) => i + 1);
        
        // Shuffle the seat numbers
        for (let i = seatNumbers.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [seatNumbers[i], seatNumbers[j]] = [seatNumbers[j], seatNumbers[i]];
        }
        
        // Create the seat assignments for this game
        const seatsForGame = playersInGame.map((player, idx) => {
          return {
            playerName: player.name,
            playerId: player.id,
            seatNumber: seatNumbers[idx],
            gamesPlayed: player.gamesPlayed
          };
        });
        
        // Sort by seat number for display
        seatsForGame.sort((a, b) => a.seatNumber - b.seatNumber);
        
        assignments.push({
          gameNumber: gameNum,
          seats: seatsForGame
        });
      }
      
      setSeatAssignments(assignments);
      showSnackbar('Seating generated successfully!', 'success');
      
      // Calculate statistics for display
      const playerStats = selectedPlayers.map(player => {
        const playerId = player.id;
        const playerName = player.nickname;
        const gamesPlayed = assignments.filter(game => 
          game.seats.some(seat => seat.playerId === playerId)
        ).length;
        const gamesList = assignments
          .filter(game => game.seats.some(seat => seat.playerId === playerId))
          .map(game => game.gameNumber);
        
        return {
          playerId,
          playerName,
          gamesPlayed,
          gamesList,
          historicalGamesPlayed: (player as any).gamesPlayed || 0
        };
      });
      
      // Sort by games played this session (ascending) then by name
      playerStats.sort((a, b) => {
        if (a.gamesPlayed !== b.gamesPlayed) {
          return a.gamesPlayed - b.gamesPlayed;
        }
        return a.playerName.localeCompare(b.playerName);
      });
      
      // Add player stats to the results
      (assignments as any).playerStats = playerStats;
      
      // Scroll to the results section after a short delay to allow for rendering
      setTimeout(() => {
        const resultsElement = document.getElementById('seat-assignments-results');
        if (resultsElement) {
          resultsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    } catch (error: any) {
      console.error('Error generating seats:', error);
      showSnackbar('Failed to generate seating: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Get the selected league name for display
  const getSelectedLeagueName = () => {
    const league = leagues.find(l => l.id === selectedLeagueId);
    return league ? league.name : '';
  };

  // Format date for display
  const formatDate = (date: Date | null) => {
    if (!date) return '';
    return date.toLocaleDateString();
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          Seat Generation Tool
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Generate random seating assignments for league games
        </Typography>
      </Box>

      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        gap: 3,
        mb: 4
      }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <FormControl sx={{ minWidth: 250, flex: '1 1 250px' }}>
            <InputLabel id="league-select-label">League</InputLabel>
            <Select
              labelId="league-select-label"
              id="league-select"
              value={selectedLeagueId}
              onChange={handleLeagueChange}
              label="League"
            >
              <MenuItem value="">
                <em>Select a league</em>
              </MenuItem>
              {leagues.map((league) => (
                <MenuItem key={league.id} value={league.id}>
                  {league.name} - {(league.clubs as any)?.name || 'Unknown Club'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Game Date"
            type="date"
            value={gameDate ? gameDate.toISOString().split('T')[0] : ''}
            onChange={(e) => {
              const date = e.target.value ? new Date(e.target.value) : null;
              setGameDate(date);
            }}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ minWidth: 250, flex: '1 1 250px' }}
          />
          
          <TextField
            type="number"
            label="Number of Games"
            value={numGames}
            onChange={handleNumGamesChange}
            InputProps={{ inputProps: { min: 1, max: 20 } }}
            sx={{ minWidth: 120, flex: '0 0 120px' }}
          />
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ flex: '1 1 300px', position: 'relative' }}>
            <Paper 
              sx={{ 
                p: 2,
                borderRadius: 1,
                boxShadow: 1,
                border: '1px solid rgba(0, 0, 0, 0.23)',
                height: '100%',
                minHeight: '400px', 
                maxHeight: '500px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Select Players ({selectedPlayers.length} selected)
              </Typography>
              
              <TextField
                placeholder="Search players..."
                variant="outlined"
                size="small"
                sx={{ mb: 1 }}
                onChange={(e) => {
                  // Filter the available players based on search
                  // This doesn't need to update state, just filters the display
                  const searchTerm = e.target.value.toLowerCase();
                  const playerList = document.getElementById('player-selection-list');
                  if (playerList) {
                    Array.from(playerList.children).forEach((item: any) => {
                      const shouldShow = item.textContent.toLowerCase().includes(searchTerm);
                      item.style.display = shouldShow ? 'flex' : 'none';
                    });
                  }
                }}
                disabled={!selectedLeagueId || loading}
              />
              
              <Box 
                id="player-selection-list"
                sx={{ 
                  overflow: 'auto', 
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5
                }}
              >
                {/* Sort players by games played (descending) */}
                {[...availablePlayers]
                  .sort((a, b) => ((b as any).gamesPlayed || 0) - ((a as any).gamesPlayed || 0))
                  .map((player) => (
                    <Box 
                      key={player.id}
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        p: 0.5,
                        borderRadius: 1,
                        '&:hover': {
                          bgcolor: 'rgba(0, 0, 0, 0.04)'
                        },
                        bgcolor: selectedPlayers.some(p => p.id === player.id) ? 'rgba(25, 118, 210, 0.08)' : 'transparent'
                      }}
                      onClick={() => {
                        const isSelected = selectedPlayers.some(p => p.id === player.id);
                        if (isSelected) {
                          // Remove player
                          handlePlayersChange(null, selectedPlayers.filter(p => p.id !== player.id));
                        } else {
                          // Add player
                          handlePlayersChange(null, [...selectedPlayers, player]);
                        }
                      }}
                    >
                      <Checkbox 
                        checked={selectedPlayers.some(p => p.id === player.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // Add player
                            handlePlayersChange(null, [...selectedPlayers, player]);
                          } else {
                            // Remove player
                            handlePlayersChange(null, selectedPlayers.filter(p => p.id !== player.id));
                          }
                        }}
                        onClick={(e) => e.stopPropagation()} // Prevent double trigger
                        disabled={!selectedLeagueId || loading}
                        size="small"
                      />
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        {player.nickname}
                      </Typography>
                      <Chip 
                        label={`${(player as any).gamesPlayed || 0} games`} 
                        size="small" 
                        variant="outlined"
                        sx={{ minWidth: '80px', fontSize: '0.7rem' }}
                      />
                      {selectedPlayers.some(p => p.id === player.id) && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            openConstraintsDialog(player);
                          }}
                          color={getPlayerConstraints(player.id).length > 0 ? "secondary" : "primary"}
                          sx={{ ml: 1, minWidth: '90px', fontSize: '0.7rem' }}
                        >
                          {getPlayerConstraints(player.id).length > 0 
                            ? `Constraints (${getPlayerConstraints(player.id).length})`
                            : "Constraints"}
                        </Button>
                      )}
                    </Box>
                  ))}
              </Box>
            </Paper>
          </Box>
        </Box>

        {!selectedLeagueId && (
          <Alert severity="info">Please select a league to continue</Alert>
        )}

        {selectedLeagueId && selectedPlayers.length > 0 && (
          <Alert severity={selectedPlayers.length >= 10 ? "success" : "warning"}>
            {selectedPlayers.length < 10 
              ? `You need at least ${10 - selectedPlayers.length} more players (minimum 10 required)`
              : `${selectedPlayers.length} players selected. You can generate seats now.`
            }
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={generateSeats}
            disabled={!selectedLeagueId || selectedPlayers.length < 10 || loading}
            sx={{ borderRadius: 2, px: 4 }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Generate'}
          </Button>
        </Box>
      </Box>


      {/* Display the generated seating assignments */}
      {seatAssignments.length > 0 && (
        <Box id="seat-assignments-results" sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Seating Assignments for {getSelectedLeagueName()}
            {gameDate && ` - ${formatDate(gameDate)}`}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            {selectedPlayers.length} players, {numGames} games, 10 players per game
          </Typography>

          {/* Player participation stats */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Player Participation Stats
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'background.default' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Player</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Historical Games</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Playing in Games</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(seatAssignments as any).playerStats?.map((stat: any) => (
                    <TableRow key={stat.playerId}>
                      <TableCell>{stat.playerName}</TableCell>
                      <TableCell>{stat.historicalGamesPlayed}</TableCell>
                      <TableCell>
                        {stat.gamesList.join(', ')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          
          {/* Approve button to save seating arrangements */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Button 
              variant="contained"
              color="primary"
              size="large"
              onClick={() => handleApproveSeating()}
              sx={{ borderRadius: 2, px: 4 }}
            >
              Approve and Save Seating
            </Button>
          </Box>
          
          {/* Detailed seating assignments - always hidden */}
          <Box id="detailed-seating" sx={{ display: 'none', mt: 3 }}>
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Detailed Seating Assignments
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {seatAssignments.map((game) => (
                <Box key={game.gameNumber}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Game #{game.gameNumber}
                  </Typography>
                  
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'background.default' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Seat</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Player</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Games Played</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {game.seats.map((seat) => (
                          <TableRow key={`${game.gameNumber}-${seat.playerId}`}>
                            <TableCell>{seat.seatNumber}</TableCell>
                            <TableCell>{seat.playerName}</TableCell>
                            <TableCell>{seat.gamesPlayed || 0}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={5000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: 10 }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      {/* Constraints Dialog */}
      <Dialog
        open={constraintsDialogOpen}
        onClose={closeConstraintsDialog}
        maxWidth="xs"
        fullWidth
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Player Constraints: {currentPlayerForConstraints?.nickname}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select games this player cannot participate in:
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {Array.from({ length: numGames }, (_, i) => i + 1).map((gameNum) => {
              const isExcluded = currentPlayerForConstraints ? 
                getPlayerConstraints(currentPlayerForConstraints.id).includes(gameNum) : false;
                
              return (
                <Chip
                  key={gameNum}
                  label={`Game ${gameNum}`}
                  onClick={() => handleToggleGameConstraint(gameNum)}
                  color={isExcluded ? "secondary" : "default"}
                  variant={isExcluded ? "filled" : "outlined"}
                  sx={{ minWidth: '80px' }}
                />
              );
            })}
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
            <Button onClick={closeConstraintsDialog}>
              Close
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Paper>
  );
};

export default SeatGenerationTool;